/**
 * Event Normalization Utilities for TestingVala QA Events.
 * Handles Price Normalization (Free/Paid/Unknown), Event Mode (online/offline/hybrid/unknown),
 * Location Normalization, and Experience Level Classification.
 *
 * Designed to be zero-dependency and 100% backward compatible.
 */

/**
 * PHASE 4: Normalize Price Values to strictly 'Free', 'Paid', or 'Unknown'
 */
export const normalizePricingType = (rawPricing) => {
  if (rawPricing === null || rawPricing === undefined) return 'Unknown';

  const str = String(rawPricing).trim().toLowerCase();

  // Free variations
  if (
    str === 'free' ||
    str === 'no cost' ||
    str === 'complimentary' ||
    str === 'free tier' ||
    str === '₹0' ||
    str === '$0' ||
    str === '0' ||
    str === 'false' ||
    str.includes('free') ||
    str.includes('no cost')
  ) {
    return 'Free';
  }

  // Paid variations
  if (
    str === 'paid' ||
    str === 'ticket' ||
    str === 'ticket required' ||
    str === 'premium' ||
    str === 'registration fee' ||
    str === 'true' ||
    str.includes('paid') ||
    str.includes('ticket') ||
    str.includes('fee') ||
    str.includes('price')
  ) {
    return 'Paid';
  }

  return 'Unknown';
};

/**
 * PHASE 2: Determine Event Mode (online, offline, hybrid, unknown)
 */
export const determineEventMode = (title = '', description = '', location = '', url = '') => {
  const combined = `${title} ${description} ${location} ${url}`.toLowerCase();

  // Online indicators
  const hasOnlineKeywords = [
    'zoom', 'google meet', 'teams', 'webex', 'youtube live', 'streamyard',
    'virtual', 'online', 'webinar', 'live stream', 'discord', 'eventbrite.com/e/',
    'lu.ma/', 'crowdcast', 'remotion', 'airmeet'
  ].some(k => combined.includes(k));

  // Physical/Offline indicators
  const hasOfflineKeywords = [
    'convention center', 'hotel', 'auditorium', 'hall', 'campus', 'building',
    'street', 'road', 'avenue', 'floor', 'room', 'in-person', 'onsite', 'on-site',
    'bengaluru', 'bangalore', 'pune', 'mumbai', 'delhi', 'noida', 'gurugram',
    'hyderabad', 'chennai', 'kolkata', 'london', 'san francisco', 'berlin'
  ].some(k => combined.includes(k));

  // Explicit check for physical location text
  const isPhysicalLocation = location && location.trim().length > 3 && 
    !['online', 'virtual', 'zoom', 'webinar', 'tbd', 'n/a'].includes(location.trim().toLowerCase());

  if (hasOnlineKeywords && (hasOfflineKeywords || isPhysicalLocation)) {
    return 'hybrid';
  }

  if (hasOnlineKeywords || (!isPhysicalLocation && !hasOfflineKeywords)) {
    return 'online';
  }

  if (hasOfflineKeywords || isPhysicalLocation) {
    return 'offline';
  }

  return 'unknown';
};

/**
 * PHASE 3: Normalize Location & Country Display
 */
export const normalizeLocation = (rawLocation = '', tags = [], description = '') => {
  const text = `${rawLocation} ${tags.join(' ')} ${description}`.toLowerCase();

  const indianCities = [
    { name: 'Bangalore', keywords: ['bengaluru', 'bangalore'] },
    { name: 'Pune', keywords: ['pune'] },
    { name: 'Mumbai', keywords: ['mumbai', 'bombay'] },
    { name: 'Delhi NCR', keywords: ['delhi', 'new delhi', 'noida', 'gurugram', 'gurgaon'] },
    { name: 'Hyderabad', keywords: ['hyderabad'] },
    { name: 'Chennai', keywords: ['chennai', 'madras'] },
    { name: 'Kolkata', keywords: ['kolkata'] },
    { name: 'Kochi', keywords: ['kochi', 'cochin'] },
    { name: 'Jaipur', keywords: ['jaipur'] },
    { name: 'Ahmedabad', keywords: ['ahmedabad'] },
  ];

  const globalCities = [
    { city: 'London', country: 'UK', keywords: ['london', 'uk', 'united kingdom'] },
    { city: 'San Francisco', country: 'USA', keywords: ['san francisco', 'sf', 'california', 'usa'] },
    { city: 'Berlin', country: 'Germany', keywords: ['berlin', 'germany'] },
    { city: 'Amsterdam', country: 'Netherlands', keywords: ['amsterdam', 'netherlands'] },
    { city: 'Singapore', country: 'Singapore', keywords: ['singapore'] },
    { city: 'Sydney', country: 'Australia', keywords: ['sydney', 'australia'] },
  ];

  // 1. Check Indian cities
  for (const cityObj of indianCities) {
    if (cityObj.keywords.some(k => text.includes(k))) {
      return {
        displayLocation: `📍 ${cityObj.name}, India`,
        city: cityObj.name,
        country: 'India',
        isIndia: true,
        isOnline: false,
      };
    }
  }

  // 2. Check Global cities
  for (const cityObj of globalCities) {
    if (cityObj.keywords.some(k => text.includes(k))) {
      return {
        displayLocation: `📍 ${cityObj.city}, ${cityObj.country}`,
        city: cityObj.city,
        country: cityObj.country,
        isIndia: false,
        isOnline: false,
      };
    }
  }

  // 3. Fallback check for India tag
  if (tags.some(t => typeof t === 'string' && t.trim().toLowerCase() === 'india') || text.includes('india')) {
    return {
      displayLocation: '📍 India',
      city: null,
      country: 'India',
      isIndia: true,
      isOnline: false,
    };
  }

  // 4. Default to Online / Global
  return {
    displayLocation: '🌐 Online',
    city: null,
    country: 'Global',
    isIndia: false,
    isOnline: true,
  };
};

/**
 * PHASE 5: Experience Level Classification
 */
export const determineExperienceLevel = (title = '', description = '', tags = []) => {
  const text = `${title} ${description} ${tags.join(' ')}`.toLowerCase();

  if (['advanced', 'expert', 'architect', 'masterclass', 'deep dive', 'internals', 'performance tuning'].some(k => text.includes(k))) {
    return 'Advanced';
  }

  if (['beginner', 'introduction', 'intro', 'basics', 'getting started', '101', 'zero to hero', 'fundamentals'].some(k => text.includes(k))) {
    return 'Beginner';
  }

  if (['intermediate', 'hands-on', 'best practices', 'patterns', 'framework'].some(k => text.includes(k))) {
    return 'Intermediate';
  }

  return 'All Levels';
};
